import { useState, useRef, useCallback } from 'react';

interface UseCountUpOptions {
    number: number; // 目标数字
    time: number; // 整体动画时长 (ms)
}

export function useCountUp({ number, time }: UseCountUpOptions) {
    const digitLength = String(number).length;
    const [curNumber, setCurNumber] = useState('0'.repeat(digitLength).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    const rafRef = useRef<number | null>(null);

    // 格式化加上千分位
    const format = (val: string) => {
        return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const run = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const targetStr = String(number);
        const start = performance.now();
        const perDigitDelay = time / digitLength; // 每位定格间隔

        const step = (now: number) => {
            const elapsed = now - start;
            let display = '';

            for (let i = 0; i < digitLength; i++) {
                const stopTime = perDigitDelay * (i + 1);
                const digitIndex = digitLength - 1 - i; // 从右往左
                const targetDigit = parseInt(targetStr[digitIndex], 10);

                if (elapsed >= stopTime) {
                    // 已经定格
                    display = targetDigit + display;
                } else {
                    if (i === digitLength - 1) {
                        // 最高位：从 0 ~ targetDigit 匀速增加
                        const progress = Math.min(elapsed / stopTime, 1);
                        const value = Math.floor(targetDigit * progress);
                        display = String(value) + display;
                    } else {
                        // 还在滚动 (根据时间算一个 0~9 的循环)
                        const speed = 30 + i * 10; // 位数越高，滚动越慢
                        const rollingDigit = Math.floor((elapsed / speed) % 10);
                        display = String(rollingDigit) + display;
                    }
                }
            }

            setCurNumber(display.padStart(digitLength, '0'));

            if (elapsed < time) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
    }, [number, time, digitLength]);

    const reset = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setCurNumber('0'.repeat(digitLength));
    }, [digitLength]);

    return { curNumber, run, reset };
}
